import Config from '@config';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  AllowedReport,
  ExportReportRequest,
  GenericModelResponse,
  GenericPreviewRequestParams,
  GenericPreviewResponse,
  GenericReportEntityResponse,
} from './types';

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/reports',
  }),
  tagTypes: ['Reports', 'AllowedReports'],
  endpoints: builder => ({
    getAllowedReportsI: builder.infiniteQuery<
      AllowedReport[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/allowed-reports/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['AllowedReports'],
    }),

    getGenericModel: builder.query<GenericModelResponse, void>({
      query: () => ({ url: '/generic/model' }),
    }),

    getGenericEntity: builder.query<
      GenericReportEntityResponse,
      { entityName: string }
    >({
      query: ({ entityName }) => ({
        url: '/generic/entity',
        params: { entityName },
      }),
    }),

    getGenericPreview: builder.query<
      GenericPreviewResponse,
      GenericPreviewRequestParams | null
    >({
      query: data => ({
        url: '/generic/preview',
        method: 'POST',
        data,
      }),
    }),

    // Returns the id of a queued export rather than the file: it is produced in the background and
    // its progress arrives over the report-export hub.
    export: builder.mutation<{ jobId: string }, ExportReportRequest>({
      query: data => ({
        url: '/generic/export',
        method: 'POST',
        data,
      }),
    }),
  }),
});

export const {
  useGetGenericModelQuery,
  useGetGenericEntityQuery,
  useGetGenericPreviewQuery,
  useExportMutation,
} = reportsApi;

export const useGetAllowedReportsInfiniteQuery = normalizeInfiniteQueryData(
  reportsApi.useGetAllowedReportsIInfiniteQuery,
);
